import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, getDocs, doc, updateDoc, deleteDoc, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig.jsx';
import { Mail, Briefcase, Search, Trash2, Eye, EyeOff, RefreshCw, Calendar, User, MessageSquare, Download, File, Paperclip, Phone, CheckCircle, XCircle, Globe } from 'lucide-react';

const HomePageManagementPage = () => {
  const [activeTab, setActiveTab] = useState('messages'); // 'messages' or 'applications'
  
  // Messages state
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(true);
  const [messagesSearchQuery, setMessagesSearchQuery] = useState('');
  const [messagesFilter, setMessagesFilter] = useState('all');
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [showMessageDetail, setShowMessageDetail] = useState(false);

  // Applications state
  const [applications, setApplications] = useState([]);
  const [applicationsLoading, setApplicationsLoading] = useState(true);
  const [applicationsSearchQuery, setApplicationsSearchQuery] = useState('');
  const [applicationsFilter, setApplicationsFilter] = useState('all');
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showApplicationDetail, setShowApplicationDetail] = useState(false);

  // Fetch Messages
  useEffect(() => {
    if (activeTab === 'messages') {
      fetchMessages();
    }
  }, [activeTab, messagesFilter, messagesSearchQuery]);

  // Fetch Applications
  useEffect(() => {
    if (activeTab === 'applications') {
      fetchApplications();
    }
  }, [activeTab, applicationsFilter, applicationsSearchQuery]);

  const fetchMessages = async () => {
    try {
      setMessagesLoading(true);
      const q = query(
        collection(db, 'contactMessages'),
        orderBy('timestamp', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const messagesData = [];
      
      querySnapshot.forEach((doc) => {
        messagesData.push({
          id: doc.id,
          ...doc.data(),
        });
      });

      let filtered = messagesData;
      
      if (messagesFilter === 'new') {
        filtered = filtered.filter(msg => !msg.read);
      } else if (messagesFilter === 'read') {
        filtered = filtered.filter(msg => msg.read);
      }

      if (messagesSearchQuery) {
        const query = messagesSearchQuery.toLowerCase();
        filtered = filtered.filter(msg => 
          msg.name?.toLowerCase().includes(query) ||
          msg.email?.toLowerCase().includes(query) ||
          msg.subject?.toLowerCase().includes(query) ||
          msg.message?.toLowerCase().includes(query)
        );
      }

      setMessages(filtered);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setMessagesLoading(false);
    }
  };

  const fetchApplications = async () => {
    try {
      setApplicationsLoading(true);
      const q = query(
        collection(db, 'jobApplications'),
        orderBy('timestamp', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const applicationsData = [];
      
      querySnapshot.forEach((doc) => {
        applicationsData.push({
          id: doc.id,
          ...doc.data(),
        });
      });

      let filtered = applicationsData;
      
      if (applicationsFilter === 'new') {
        filtered = filtered.filter(app => !app.reviewed);
      } else if (applicationsFilter === 'reviewed') {
        filtered = filtered.filter(app => app.reviewed && app.status === 'reviewed');
      } else if (applicationsFilter === 'rejected') {
        filtered = filtered.filter(app => app.status === 'rejected');
      }

      if (applicationsSearchQuery) {
        const query = applicationsSearchQuery.toLowerCase();
        filtered = filtered.filter(app => 
          app.name?.toLowerCase().includes(query) ||
          app.email?.toLowerCase().includes(query) ||
          app.position?.toLowerCase().includes(query) ||
          app.phone?.toLowerCase().includes(query)
        );
      }

      setApplications(filtered);
    } catch (error) {
      console.error('Error fetching applications:', error);
    } finally {
      setApplicationsLoading(false);
    }
  };

  // Messages functions
  const markAsRead = async (messageId) => {
    try {
      await updateDoc(doc(db, 'contactMessages', messageId), {
        read: true,
        readAt: Timestamp.now(),
      });
      fetchMessages();
      if (selectedMessage?.id === messageId) {
        setSelectedMessage({ ...selectedMessage, read: true });
      }
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const markAsUnread = async (messageId) => {
    try {
      await updateDoc(doc(db, 'contactMessages', messageId), {
        read: false,
        readAt: null,
      });
      fetchMessages();
      if (selectedMessage?.id === messageId) {
        setSelectedMessage({ ...selectedMessage, read: false });
      }
    } catch (error) {
      console.error('Error marking as unread:', error);
    }
  };

  const deleteMessage = async (messageId) => {
    if (!window.confirm('이 메시지를 삭제하시겠습니까?')) return;
    
    try {
      await deleteDoc(doc(db, 'contactMessages', messageId));
      fetchMessages();
      if (selectedMessage?.id === messageId) {
        setSelectedMessage(null);
        setShowMessageDetail(false);
      }
    } catch (error) {
      console.error('Error deleting message:', error);
      alert('메시지 삭제 중 오류가 발생했습니다.');
    }
  };

  const openMessageDetail = (message) => {
    setSelectedMessage(message);
    setShowMessageDetail(true);
    if (!message.read) {
      markAsRead(message.id);
    }
  };

  // Applications functions
  const markAsReviewed = async (id) => {
    try {
      await updateDoc(doc(db, 'jobApplications', id), {
        reviewed: true,
        status: 'reviewed',
      });
      fetchApplications();
      if (selectedApplication?.id === id) {
        setSelectedApplication({ ...selectedApplication, reviewed: true, status: 'reviewed' });
      }
    } catch (error) {
      console.error('Error marking as reviewed:', error);
    }
  };

  const markAsRejected = async (id) => {
    try {
      await updateDoc(doc(db, 'jobApplications', id), {
        reviewed: true,
        status: 'rejected',
      });
      fetchApplications();
      if (selectedApplication?.id === id) {
        setSelectedApplication({ ...selectedApplication, reviewed: true, status: 'rejected' });
      }
    } catch (error) {
      console.error('Error marking as rejected:', error);
    }
  };

  const deleteApplication = async (id) => {
    if (!window.confirm('정말 이 지원서를 삭제하시겠습니까?')) return;
    
    try {
      await deleteDoc(doc(db, 'jobApplications', id));
      fetchApplications();
      if (selectedApplication?.id === id) {
        setShowApplicationDetail(false);
        setSelectedApplication(null);
      }
    } catch (error) {
      console.error('Error deleting application:', error);
    }
  };

  const openApplicationDetail = (application) => {
    setSelectedApplication(application);
    setShowApplicationDetail(true);
    if (!application.reviewed) {
      markAsReviewed(application.id);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '날짜 없음';
    if (timestamp.toDate) {
      return timestamp.toDate().toLocaleString('ko-KR');
    }
    if (timestamp instanceof Date) {
      return timestamp.toLocaleString('ko-KR');
    }
    return new Date(timestamp).toLocaleString('ko-KR');
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const unreadMessagesCount = messages.filter(msg => !msg.read).length;
  const newApplicationsCount = applications.filter(app => !app.reviewed).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-white/20">
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-2xl shadow-lg">
                <Globe className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-4xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                  HomePage Management
                </h1>
                <p className="text-gray-600 text-lg mt-1">홈페이지 문의 메시지 및 채용 지원서 관리</p>
              </div>
            </div>
            <button
              onClick={() => activeTab === 'messages' ? fetchMessages() : fetchApplications()}
              className="p-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl transition-colors"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 border-b border-gray-200">
            <button
              onClick={() => setActiveTab('messages')}
              className={`px-6 py-3 font-medium transition-colors relative ${
                activeTab === 'messages'
                  ? 'text-indigo-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center gap-2">
                <Mail className="w-5 h-5" />
                <span>Contact Messages</span>
                {unreadMessagesCount > 0 && (
                  <span className="px-2 py-0.5 bg-indigo-600 text-white text-xs rounded-full">
                    {unreadMessagesCount}
                  </span>
                )}
              </div>
              {activeTab === 'messages' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600"></div>
              )}
            </button>
            <button
              onClick={() => setActiveTab('applications')}
              className={`px-6 py-3 font-medium transition-colors relative ${
                activeTab === 'applications'
                  ? 'text-indigo-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center gap-2">
                <Briefcase className="w-5 h-5" />
                <span>Job Applications</span>
                {newApplicationsCount > 0 && (
                  <span className="px-2 py-0.5 bg-blue-600 text-white text-xs rounded-full">
                    {newApplicationsCount}
                  </span>
                )}
              </div>
              {activeTab === 'applications' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600"></div>
              )}
            </button>
          </div>
        </div>

        {/* Messages Tab Content */}
        {activeTab === 'messages' && (
          <>
            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-xl">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">전체 메시지</p>
                    <p className="text-2xl font-bold text-gray-900">{messages.length}</p>
                  </div>
                  <MessageSquare className="w-8 h-8 text-indigo-600" />
                </div>
              </div>
              <div className="bg-gradient-to-r from-yellow-50 to-orange-50 p-4 rounded-xl">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">읽지 않음</p>
                    <p className="text-2xl font-bold text-gray-900">{unreadMessagesCount}</p>
                  </div>
                  <EyeOff className="w-8 h-8 text-orange-600" />
                </div>
              </div>
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-xl">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">읽음</p>
                    <p className="text-2xl font-bold text-gray-900">{messages.length - unreadMessagesCount}</p>
                  </div>
                  <Eye className="w-8 h-8 text-emerald-600" />
                </div>
              </div>
            </div>

            {/* Search and Filter */}
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="이름, 이메일, 제목, 내용으로 검색..."
                  value={messagesSearchQuery}
                  onChange={(e) => setMessagesSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setMessagesFilter('all')}
                  className={`px-4 py-3 rounded-xl font-medium transition-colors ${
                    messagesFilter === 'all' 
                      ? 'bg-indigo-600 text-white' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  전체
                </button>
                <button
                  onClick={() => setMessagesFilter('new')}
                  className={`px-4 py-3 rounded-xl font-medium transition-colors ${
                    messagesFilter === 'new' 
                      ? 'bg-indigo-600 text-white' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  읽지 않음 ({unreadMessagesCount})
                </button>
                <button
                  onClick={() => setMessagesFilter('read')}
                  className={`px-4 py-3 rounded-xl font-medium transition-colors ${
                    messagesFilter === 'read' 
                      ? 'bg-indigo-600 text-white' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  읽음
                </button>
              </div>
            </div>

            {/* Messages List */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-4">
                {messagesLoading ? (
                  <div className="flex justify-center items-center h-64">
                    <RefreshCw className="w-8 h-8 animate-spin text-indigo-600" />
                  </div>
                ) : messages.length === 0 ? (
                  <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-12 text-center shadow-xl border border-white/20">
                    <Mail className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 text-lg">메시지가 없습니다.</p>
                  </div>
                ) : (
                  messages.map((message) => (
                    <div
                      key={message.id}
                      onClick={() => openMessageDetail(message)}
                      className={`bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg border cursor-pointer transition-all hover:shadow-xl ${
                        !message.read 
                          ? 'border-indigo-300 bg-indigo-50/50' 
                          : 'border-gray-200'
                      }`}
                    >
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="font-bold text-lg text-gray-900">{message.subject}</h3>
                            {!message.read && (
                              <span className="px-2 py-1 bg-indigo-600 text-white text-xs rounded-full">
                                새 메시지
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-4 text-sm text-gray-600 mb-2">
                            <div className="flex items-center gap-1">
                              <User className="w-4 h-4" />
                              <span>{message.name}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Mail className="w-4 h-4" />
                              <span>{message.email}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          {message.read ? (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                markAsUnread(message.id);
                              }}
                              className="p-2 text-gray-400 hover:text-indigo-600 transition-colors"
                              title="읽지 않음으로 표시"
                            >
                              <EyeOff className="w-4 h-4" />
                            </button>
                          ) : (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                markAsRead(message.id);
                              }}
                              className="p-2 text-gray-400 hover:text-green-600 transition-colors"
                              title="읽음으로 표시"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteMessage(message.id);
                            }}
                            className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                            title="삭제"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      <p className="text-gray-700 line-clamp-2 mb-3">{message.message}</p>
                      {message.attachments && message.attachments.length > 0 && (
                        <div className="flex items-center gap-2 mb-3">
                          <Paperclip className="w-4 h-4 text-gray-400" />
                          <span className="text-xs text-gray-600">
                            첨부파일 {message.attachments.length}개
                          </span>
                        </div>
                      )}
                      <div className="flex items-center gap-1 text-xs text-gray-500">
                        <Calendar className="w-3 h-3" />
                        <span>{formatDate(message.timestamp)}</span>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Message Detail */}
              <div className="lg:col-span-1">
                {showMessageDetail && selectedMessage ? (
                  <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-6 shadow-xl border border-white/20 sticky top-6">
                    <div className="flex justify-between items-start mb-4">
                      <h2 className="text-2xl font-bold text-gray-900">메시지 상세</h2>
                      <button
                        onClick={() => {
                          setShowMessageDetail(false);
                          setSelectedMessage(null);
                        }}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        ✕
                      </button>
                    </div>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="text-sm font-semibold text-gray-600">제목</label>
                        <p className="text-lg font-bold text-gray-900 mt-1">{selectedMessage.subject}</p>
                      </div>
                      
                      <div>
                        <label className="text-sm font-semibold text-gray-600">이름</label>
                        <p className="text-gray-900 mt-1">{selectedMessage.name}</p>
                      </div>
                      
                      <div>
                        <label className="text-sm font-semibold text-gray-600">이메일</label>
                        <p className="text-gray-900 mt-1">
                          <a 
                            href={`mailto:${selectedMessage.email}`}
                            className="text-indigo-600 hover:underline"
                          >
                            {selectedMessage.email}
                          </a>
                        </p>
                      </div>
                      
                      <div>
                        <label className="text-sm font-semibold text-gray-600">메시지</label>
                        <div className="mt-2 p-4 bg-gray-50 rounded-xl text-gray-900 whitespace-pre-wrap">
                          {selectedMessage.message}
                        </div>
                      </div>
                      
                      {selectedMessage.attachments && selectedMessage.attachments.length > 0 && (
                        <div>
                          <label className="text-sm font-semibold text-gray-600 mb-2 block">
                            첨부 파일 ({selectedMessage.attachments.length})
                          </label>
                          <div className="space-y-2">
                            {selectedMessage.attachments.map((attachment, index) => (
                              <div
                                key={index}
                                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors"
                              >
                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                  <div className="p-2 bg-indigo-100 rounded-lg">
                                    <File className="w-4 h-4 text-indigo-600" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-900 truncate">
                                      {attachment.name}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                      {attachment.size ? formatFileSize(attachment.size) : ''}
                                    </p>
                                  </div>
                                </div>
                                <a
                                  href={attachment.url}
                                  download={attachment.name}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="ml-2 p-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
                                  title="다운로드"
                                >
                                  <Download className="w-4 h-4" />
                                </a>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      <div>
                        <label className="text-sm font-semibold text-gray-600">전송 시간</label>
                        <p className="text-gray-900 mt-1">{formatDate(selectedMessage.timestamp)}</p>
                      </div>
                      
                      <div className="flex gap-2 pt-4 border-t">
                        {selectedMessage.read ? (
                          <button
                            onClick={() => markAsUnread(selectedMessage.id)}
                            className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
                          >
                            읽지 않음으로 표시
                          </button>
                        ) : (
                          <button
                            onClick={() => markAsRead(selectedMessage.id)}
                            className="flex-1 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
                          >
                            읽음으로 표시
                          </button>
                        )}
                        <button
                          onClick={() => deleteMessage(selectedMessage.id)}
                          className="px-4 py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg transition-colors"
                        >
                          삭제
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-12 text-center shadow-xl border border-white/20">
                    <MessageSquare className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">메시지를 선택하여 상세 내용을 확인하세요.</p>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* Applications Tab Content */}
        {activeTab === 'applications' && (
          <>
            {/* Search and Filter */}
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="이름, 이메일, 전화번호, 지원 분야로 검색..."
                  value={applicationsSearchQuery}
                  onChange={(e) => setApplicationsSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setApplicationsFilter('all')}
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    applicationsFilter === 'all' ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  전체
                </button>
                <button
                  onClick={() => setApplicationsFilter('new')}
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    applicationsFilter === 'new' ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  신규
                </button>
                <button
                  onClick={() => setApplicationsFilter('reviewed')}
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    applicationsFilter === 'reviewed' ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  검토 완료
                </button>
                <button
                  onClick={() => setApplicationsFilter('rejected')}
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    applicationsFilter === 'rejected' ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  거절
                </button>
              </div>
            </div>

            {/* Applications List */}
            <div className="grid grid-cols-1 gap-4">
              {applicationsLoading ? (
                <div className="flex justify-center items-center h-64">
                  <RefreshCw className="w-8 h-8 animate-spin text-primary-600" />
                </div>
              ) : applications.length === 0 ? (
                <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-12 text-center shadow-xl border border-white/20">
                  <Briefcase className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 text-lg">지원서가 없습니다.</p>
                </div>
              ) : (
                applications.map((application) => {
                  const getStatusBadge = () => {
                    if (!application.reviewed) {
                      return <span className="px-2 py-1 bg-blue-600 text-white text-xs rounded-full">신규</span>;
                    }
                    if (application.status === 'reviewed') {
                      return <span className="px-2 py-1 bg-green-600 text-white text-xs rounded-full">검토 완료</span>;
                    }
                    if (application.status === 'rejected') {
                      return <span className="px-2 py-1 bg-red-600 text-white text-xs rounded-full">거절</span>;
                    }
                    return null;
                  };

                  return (
                    <div
                      key={application.id}
                      onClick={() => openApplicationDetail(application)}
                      className={`bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg border cursor-pointer transition-all hover:shadow-xl ${
                        !application.reviewed 
                          ? 'border-blue-300 bg-blue-50/50' 
                          : 'border-gray-200'
                      }`}
                    >
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="font-bold text-lg text-gray-900">{application.name}</h3>
                            {getStatusBadge()}
                          </div>
                          <div className="flex items-center gap-4 text-sm text-gray-600 mb-2">
                            <div className="flex items-center gap-1">
                              <Mail className="w-4 h-4" />
                              <span>{application.email}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Phone className="w-4 h-4" />
                              <span>{application.phone}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Briefcase className="w-4 h-4" />
                              <span>{application.position || '미지정'}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          {application.reviewed ? (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                markAsRejected(application.id);
                              }}
                              className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                              title="거절로 표시"
                            >
                              <XCircle className="w-4 h-4" />
                            </button>
                          ) : (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                markAsReviewed(application.id);
                              }}
                              className="p-2 text-blue-600 hover:text-blue-700 transition-colors"
                              title="검토 완료로 표시"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteApplication(application.id);
                            }}
                            className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                            title="삭제"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      {application.coverLetter && (
                        <p className="text-gray-700 line-clamp-2 mb-3">{application.coverLetter}</p>
                      )}
                      {application.resume && (
                        <div className="flex items-center gap-2 mb-3">
                          <File className="w-4 h-4 text-gray-400" />
                          <span className="text-xs text-gray-600">이력서 첨부됨</span>
                        </div>
                      )}
                      <div className="flex items-center gap-1 text-xs text-gray-500">
                        <Calendar className="w-3 h-3" />
                        <span>{formatDate(application.timestamp)}</span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Application Detail Modal */}
            {showApplicationDetail && selectedApplication && (
              <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowApplicationDetail(false)}>
                <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                  <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex justify-between items-center">
                    <h2 className="text-2xl font-bold text-gray-900">지원서 상세</h2>
                    <button
                      onClick={() => setShowApplicationDetail(false)}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  
                  <div className="p-6 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-semibold text-gray-600 mb-2 block">이름</label>
                        <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                          <User className="w-4 h-4 text-gray-500" />
                          <span className="text-gray-900">{selectedApplication.name}</span>
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-semibold text-gray-600 mb-2 block">이메일</label>
                        <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                          <Mail className="w-4 h-4 text-gray-500" />
                          <span className="text-gray-900">{selectedApplication.email}</span>
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-semibold text-gray-600 mb-2 block">전화번호</label>
                        <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                          <Phone className="w-4 h-4 text-gray-500" />
                          <span className="text-gray-900">{selectedApplication.phone}</span>
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-semibold text-gray-600 mb-2 block">지원 분야</label>
                        <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                          <Briefcase className="w-4 h-4 text-gray-500" />
                          <span className="text-gray-900">{selectedApplication.position || '미지정'}</span>
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="text-sm font-semibold text-gray-600 mb-2 block">자기소개서</label>
                      <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <p className="text-gray-700 whitespace-pre-wrap">{selectedApplication.coverLetter || '없음'}</p>
                      </div>
                    </div>

                    {selectedApplication.resume && (
                      <div>
                        <label className="text-sm font-semibold text-gray-600 mb-2 block">이력서</label>
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors">
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="p-2 bg-blue-100 rounded-lg">
                              <File className="w-4 h-4 text-blue-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {selectedApplication.resume.name}
                              </p>
                              <p className="text-xs text-gray-500">
                                {selectedApplication.resume.size ? formatFileSize(selectedApplication.resume.size) : ''}
                              </p>
                            </div>
                          </div>
                          <a
                            href={selectedApplication.resume.url}
                            download={selectedApplication.resume.name}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="ml-2 p-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                            title="다운로드"
                          >
                            <Download className="w-4 h-4" />
                          </a>
                        </div>
                      </div>
                    )}

                    <div>
                      <label className="text-sm font-semibold text-gray-600 mb-2 block">제출 일시</label>
                      <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                        <Calendar className="w-4 h-4 text-gray-500" />
                        <span className="text-gray-900">{formatDate(selectedApplication.timestamp)}</span>
                      </div>
                    </div>

                    <div className="flex gap-3 pt-4 border-t border-gray-200">
                      {!selectedApplication.reviewed ? (
                        <button
                          onClick={() => {
                            markAsReviewed(selectedApplication.id);
                            setShowApplicationDetail(false);
                          }}
                          className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                        >
                          검토 완료로 표시
                        </button>
                      ) : (
                        <button
                          onClick={() => {
                            markAsRejected(selectedApplication.id);
                            setShowApplicationDetail(false);
                          }}
                          className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                        >
                          거절로 표시
                        </button>
                      )}
                      <button
                        onClick={() => {
                          deleteApplication(selectedApplication.id);
                          setShowApplicationDetail(false);
                        }}
                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                      >
                        삭제
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default HomePageManagementPage;

